import "@/index.css";
import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import { 
  Code2, 
  Users, 
  Shield, 
  BarChart3, 
  ArrowRight, 
  ChevronDown,
  Globe,
  Lock,
  Zap,
  Handshake,
  Building2,
  Trophy,
  Mail,
  ExternalLink,
  MapPin,
  Menu,
  X,
  Calendar,
  Clock,
  ArrowLeft,
  Tag,
  CheckCircle
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_14c5d26a-bc3a-4de9-9ecb-37c676ee6d9c/artifacts/ctttb0sh_logo.webp";

// SEO Component
const SEO = ({ title, description, keywords }) => {
  useEffect(() => {
    document.title = title || 'ARK Global Solutions';
    
    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', description || '');
    }
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords || '');
    }
  }, [title, description, keywords]);
  
  return null;
};

// Import blog data
import { blogPosts } from "./data/blogData";

// Navigation Component
const Navigation = ({ isHome = true }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogoClick = (e) => {
    if (isHome) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navLinks = isHome ? [
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Projects", href: "#projects" },
    { name: "Why Us", href: "#why-us" },
    { name: "Blog", href: "/blog" },
    { name: "Careers", href: "/careers" },
    { name: "Contact", href: "/contact" },
  ] : [
    { name: "Home", href: "/" },
    { name: "Blog", href: "/blog" },
    { name: "Careers", href: "/careers" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-lg border-b border-gray-200" : "bg-white/95 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <Link to="/" onClick={handleLogoClick} data-testid="logo-link" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="ARK Global Solutions" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.href.startsWith('#') ? (
                <a
                  key={link.name}
                  href={link.href}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  className="text-sm font-medium text-gray-700 hover:text-[#2563eb] transition-colors"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  className="text-sm font-medium text-gray-700 hover:text-[#2563eb] transition-colors"
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="bg-[#2563eb] hover:bg-[#3b82f6] text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all shadow-[0_0_15px_rgba(37,99,235,0.4)] hover:shadow-[0_0_25px_rgba(37,99,235,0.6)]"
            >
              Get in Touch
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden text-gray-700 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div data-testid="mobile-menu" className="md:hidden pb-6 border-t border-gray-200 mt-4 pt-4">
            {navLinks.map((link) => (
              link.href.startsWith('#') ? (
                <a
                  key={link.name}
                  href={link.href}
                  className="block py-3 text-gray-700 hover:text-[#2563eb] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className="block py-3 text-gray-700 hover:text-[#2563eb] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link
              to="/contact"
              className="block mt-4 bg-[#2563eb] text-white px-6 py-3 rounded-full text-center font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Get in Touch
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0A0A0A]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2563eb]/20 via-transparent to-[#3b82f6]/10" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#2563eb]/30 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#60a5fa]/20 rounded-full blur-[96px]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-32 pb-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="fade-in-up">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight mb-6">
              Building <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563eb] to-[#60a5fa]">Solutions</span> that Drive Business Forward
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-8 max-w-lg">
              ARK Global Solutions Ltd delivers bespoke software development, staffing, HR management, and business support services to organisations across the UK and beyond.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#projects"
                data-testid="hero-cta-projects"
                className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#3b82f6] text-white px-8 py-4 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:shadow-[0_0_30px_rgba(37,99,235,0.6)]"
              >
                View Our Projects <ArrowRight size={18} />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-services"
                className="inline-flex items-center gap-2 bg-transparent border border-white/20 text-white hover:bg-white/10 px-8 py-4 rounded-full font-medium transition-all"
              >
                Our Services
              </a>
            </div>
          </div>

          <div className="fade-in-up delay-200 hidden md:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#2563eb] to-[#60a5fa] rounded-2xl opacity-20 blur-xl" />
              <div className="relative glass rounded-2xl p-8">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-4xl font-bold text-[#60a5fa]">4</div>
                    <div className="text-sm text-gray-400 mt-1">Core Services</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-white">2+</div>
                    <div className="text-sm text-gray-400 mt-1">Live Projects</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-[#2563eb]">UK</div>
                    <div className="text-sm text-gray-400 mt-1">Based</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="text-gray-500" size={28} />
        </div>
      </div>

      {/* Marquee */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#111111] border-y border-white/5 py-4 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-8 px-4">
              {["Software Development", "Staffing Solutions", "HR Management", "UKVI Compliance", "Business Support", "Mobile Apps", "Web Applications", "HR Compliance"].map((item, idx) => (
                <span key={idx} className="text-gray-500 text-sm font-medium">
                  {item} <span className="text-[#60a5fa] mx-4">•</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  const capabilities = [
    { icon: Code2, title: "Software Dev", desc: "Custom web and mobile applications tailored to your business needs" },
    { icon: Users, title: "Staffing", desc: "Skilled temporary and contract workers placed rapidly" },
    { icon: Shield, title: "HR & Compliance", desc: "Full HR management including UKVI sponsor licence support" },
    { icon: BarChart3, title: "Business Support", desc: "Operational support, admin, and back-office business services" },
  ];

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#60a5fa] text-sm font-medium tracking-wider uppercase mb-4">Who We Are</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              A multi-discipline solutions <span className="text-[#60a5fa]">partner</span>
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              ARK Global Solutions Ltd is a UK-registered company operating across four key business sectors — software development, temporary staffing, HR management, and broader business support services.
            </p>
            <p className="text-gray-400 leading-relaxed">
              We partner with businesses to deliver technology, people, and process solutions — whether that means building a custom application, placing skilled talent, managing HR compliance, or supporting day-to-day operations.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {capabilities.map((item, idx) => (
              <div
                key={idx}
                data-testid={`capability-${item.title.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className="group p-6 rounded-xl bg-[#111111] border border-white/5 hover:border-[#2563eb]/50 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-lg bg-[#2563eb]/20 flex items-center justify-center mb-4 group-hover:bg-[#2563eb]/30 transition-colors">
                  <item.icon className="text-[#60a5fa]" size={24} strokeWidth={1.5} />
                </div>
                <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Service Categories */}
        <div className="mt-20 grid md:grid-cols-3 gap-6">
          {[
            { title: "Software Development", desc: "Bespoke applications built for commercial clients and internal use. Web platforms, mobile apps, APIs, and SaaS products." },
            { title: "Staffing & HR Management", desc: "Temporary employment, candidate sourcing, and complete human resources management including UKVI compliance for sponsored workers." },
            { title: "Business Support Services", desc: "Operational, administrative, and business support activities helping organisations run more efficiently." },
          ].map((item, idx) => (
            <div
              key={idx}
              data-testid={`service-category-${idx}`}
              className="p-6 rounded-xl glass hover:border-[#60a5fa]/30 transition-all duration-300"
            >
              <h3 className="font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-sm text-gray-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const services = [
    {
      num: "01",
      icon: Code2,
      title: "Software Development",
      subtitle: "Bespoke Software Development",
      desc: "We design and build custom software solutions — from mobile apps and web platforms to full SaaS products — for clients across sectors.",
      features: [
        "Web application development (React, Next.js, Node.js)",
        "Mobile apps — iOS & Android (React Native)",
        "API integration & backend systems",
        "SaaS product development",
        "Database design & cloud infrastructure (AWS)",
        "AI-powered features & integrations"
      ]
    },
    {
      num: "02",
      icon: Users,
      title: "Temporary Staffing",
      subtitle: "Temporary Employment & Recruitment",
      desc: "We source, vet, and place skilled workers into temporary and contract roles for businesses requiring flexible workforce solutions.",
      features: [
        "Temporary & contract worker placement",
        "Technical & IT staffing",
        "Skills-matched candidate sourcing",
        "Rapid deployment for business needs",
        "Payroll management for placed workers",
        "Right to Work verification"
      ]
    },
    {
      num: "03",
      icon: Shield,
      title: "HR Management",
      subtitle: "HR & UKVI Compliance",
      desc: "Complete human resources provision including UKVI sponsor licence management, Right to Work compliance, and employment documentation.",
      features: [
        "UKVI sponsor licence management",
        "Right to Work checks & audit trails",
        "Visa & document expiry tracking",
        "Employment contracts & letters",
        "Payroll administration & leave management",
        "OISC immigration advisor coordination"
      ]
    },
    {
      num: "04",
      icon: BarChart3,
      title: "Business Support",
      subtitle: "Business Support Services",
      desc: "Operational and administrative support services helping businesses run efficiently, from back-office functions to strategic business process support.",
      features: [
        "Business process support & optimisation",
        "Administrative services & coordination",
        "Compliance reporting & documentation",
        "Project management support",
        "Client account management",
        "Operational consultancy"
      ]
    }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#111111] relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <p className="text-[#60a5fa] text-sm font-medium tracking-wider uppercase mb-4">What We Do</p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
            Our <span className="text-[#60a5fa]">Services</span>
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Four interconnected disciplines, one trusted partner — we deliver across the full business lifecycle.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, idx) => (
            <div
              key={idx}
              data-testid={`service-card-${service.num}`}
              className="group relative overflow-hidden rounded-2xl bg-black/40 p-8 border border-white/10 hover:border-[#60a5fa]/50 transition-all duration-500"
            >
              <div className="absolute top-0 right-0 text-[120px] font-bold text-white/[0.02] leading-none">
                {service.num}
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#2563eb] to-[#3b82f6] flex items-center justify-center">
                    <service.icon className="text-white" size={28} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{service.title}</h3>
                  </div>
                </div>

                <h4 className="text-lg font-medium text-gray-200 mb-3">{service.subtitle}</h4>
                <p className="text-gray-400 mb-6">{service.desc}</p>

                <ul className="space-y-2">
                  {service.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-start gap-2 text-sm text-gray-400">
                      <span className="text-[#60a5fa] mt-1">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Projects Section
const Projects = () => {
  const projects = [
    {
      icon: Shield,
      status: "In Active Development",
      statusColor: "bg-green-500",
      name: "STAYRYTE",
      client: "In-House Product · ARK Global Solutions Ltd",
      desc: "STAYRYTE is ARK Global's own SaaS platform — the UK's first combined HR management and UKVI immigration compliance system. Featuring Right to Work tracking, eVisa alerts, encrypted bank details, an OISC advisor marketplace, and AI-powered letter generation.",
      tech: ["React.js", "Next.js", "Node.js", "Supabase", "Stripe", "Claude AI", "AES-256"],
      progress: 75
    },
    {
      icon: Globe,
      status: "In Development",
      statusColor: "bg-[#60a5fa]",
      name: "Travel App",
      client: "Client: Qwizt IT Solutions Ltd",
      desc: "A full-stack travel booking platform — mobile app (iOS & Android) and web application — enabling users to search, plan and book flights, hotels and travel services with real-time availability.",
      tech: ["React Native", "React.js", "Node.js", "PostgreSQL", "AWS", "Stripe"],
      progress: 55
    }
  ];

  return (
    <section id="projects" data-testid="projects-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <p className="text-[#60a5fa] text-sm font-medium tracking-wider uppercase mb-4">What We're Building</p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
            Ongoing <span className="text-[#60a5fa]">Projects</span>
          </h2>
          <p className="text-gray-400 mt-4">
            Live work currently in active development at ARK Global Solutions Ltd.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {projects.map((project, idx) => (
            <div
              key={idx}
              data-testid={`project-card-${project.name.toLowerCase().replace(' ', '-')}`}
              className="group relative rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-[#2563eb]/50 transition-all duration-500 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#2563eb]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative z-10 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#2563eb]/30 flex items-center justify-center">
                    <project.icon className="text-[#60a5fa]" size={24} strokeWidth={1.5} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${project.statusColor}`} />
                    <span className="text-xs text-gray-400">{project.status}</span>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-2">{project.name}</h3>
                <p className="text-sm text-[#60a5fa] mb-4">{project.client}</p>
                <p className="text-gray-400 mb-6">{project.desc}</p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tech.map((tech, tIdx) => (
                    <span 
                      key={tIdx}
                      className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-gray-300"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">Development Progress</span>
                    <span className="text-[#60a5fa] font-medium">{project.progress}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#2563eb] to-[#60a5fa] rounded-full progress-animated"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyUs = () => {
  const reasons = [
    { icon: Building2, num: "01", title: "Full-Stack Capability", desc: "From initial concept to production deployment — we handle design, development, infrastructure, and ongoing support across web and mobile platforms." },
    { icon: Globe, num: "02", title: "UK Regulatory Expertise", desc: "Deep knowledge of UKVI rules, sponsor licence obligations, Right to Work requirements, and UK employment law — built into everything we do." },
    { icon: Lock, num: "03", title: "Security First", desc: "Enterprise-grade security in all our platforms — AES-256 encryption, role-based access control, audit logging, and GDPR-compliant data handling as standard." },
    { icon: Zap, num: "04", title: "Agile & Transparent", desc: "We work in iterative sprints with regular client updates, clear roadmaps, and full visibility into progress — no black boxes, no surprises." },
    { icon: Trophy, num: "05", title: "Multi-Sector Reach", desc: "Operating across multiple categories, we bring cross-sector perspective to every engagement — technology informs our HR work, and HR knowledge shapes our software." },
    { icon: Handshake, num: "06", title: "Long-Term Partnership", desc: "We build lasting relationships — not just delivering projects but growing with our clients, adapting our services as their business evolves and scales." },
  ];

  return (
    <section id="why-us" data-testid="why-us-section" className="py-24 md:py-32 bg-[#111111] relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <p className="text-[#60a5fa] text-sm font-medium tracking-wider uppercase mb-4">Why Choose Us</p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
            The ARK Global <span className="text-[#60a5fa]">Difference</span>
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            We combine technical depth, regulatory knowledge, and genuine partnership to deliver outcomes that matter.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((reason, idx) => (
            <div
              key={idx}
              data-testid={`why-us-card-${reason.num}`}
              className="group p-6 rounded-xl bg-black/40 border border-white/5 hover:border-[#2563eb]/50 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#2563eb]/20 flex items-center justify-center group-hover:bg-[#2563eb]/30 transition-colors">
                  <reason.icon className="text-[#60a5fa]" size={22} strokeWidth={1.5} />
                </div>
                <div>
                  <span className="text-xs text-[#60a5fa] font-mono">{reason.num}</span>
                  <h3 className="font-semibold text-white mb-2">{reason.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{reason.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Page (Separate from landing)
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    website: '' // Honeypot field - bots will fill this
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStartTime, setFormStartTime] = useState(Date.now());

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setFormStartTime(Date.now());
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Spam protection checks
    // 1. Honeypot check - if website field is filled, it's a bot
    if (formData.website) {
      setStatus({ type: 'success', message: 'Thank you for your message. We\'ll get back to you soon!' });
      return;
    }
    
    // 2. Time-based check - form filled too quickly (under 3 seconds) is likely a bot
    const timeTaken = Date.now() - formStartTime;
    if (timeTaken < 3000) {
      setStatus({ type: 'success', message: 'Thank you for your message. We\'ll get back to you soon!' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    // Remove honeypot field before sending
    const { website, ...submitData } = formData;

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: data.message });
        setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '', website: '' });
        setFormStartTime(Date.now());
      } else {
        setStatus({ type: 'error', message: data.detail || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <SEO 
        title="Contact Us | ARK Global Solutions - Get in Touch"
        description="Contact ARK Global Solutions for software development, staffing, HR management and UKVI compliance services. Based at 124 City Road, London EC1V 2NX."
        keywords="contact ARK Global, software development enquiry, staffing solutions contact, HR compliance consultation, London software company"
      />
      <Navigation isHome={false} />
      
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2563eb]/20 via-transparent to-[#60a5fa]/10" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <p className="text-[#60a5fa] text-sm font-medium tracking-wider uppercase mb-4">Get in Touch</p>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            <span className="text-[#60a5fa]">Contact</span> Us
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl">
            Whether you need a software partner, staffing support, HR management, or business services — let's talk about what ARK Global can do for you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section data-testid="contact-section" className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="rounded-2xl bg-white/5 border border-white/10 p-8">
              <h2 className="text-xl font-semibold text-white mb-6">Send us a message</h2>
              
              {status.message && (
                <div 
                  data-testid="form-status"
                  className={`mb-6 p-4 rounded-lg ${
                    status.type === 'success' 
                      ? 'bg-green-500/20 border border-green-500/30 text-green-400' 
                      : 'bg-red-500/20 border border-red-500/30 text-red-400'
                  }`}
                >
                  {status.message}
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
              {/* Honeypot field - hidden from users, bots will fill it */}
              <div className="absolute -left-[9999px]" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  name="website"
                  id="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name"
                    className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#60a5fa]/50 transition-colors"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email"
                    className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#60a5fa]/50 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="contact-phone"
                    className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#60a5fa]/50 transition-colors"
                    placeholder="+44 123 456 7890"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    data-testid="contact-company"
                    className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#60a5fa]/50 transition-colors"
                    placeholder="Your company name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Service Interest</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  data-testid="contact-service"
                  className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white focus:outline-none focus:border-[#60a5fa]/50 transition-colors"
                >
                  <option value="">Select a service</option>
                  <option value="Software Development">Software Development</option>
                  <option value="Staffing Solutions">Staffing Solutions</option>
                  <option value="HR Management">HR Management</option>
                  <option value="Business Support">Business Support</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  data-testid="contact-message"
                  className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#60a5fa]/50 transition-colors resize-none"
                  placeholder="Tell us about your project or inquiry..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit"
                className="w-full bg-[#2563eb] hover:bg-[#3b82f6] disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full font-bold transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)]"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col justify-center">
            <div className="rounded-2xl bg-gradient-to-br from-[#2563eb] to-[#1e40af] p-8 mb-6">
              <h3 className="text-xl font-semibold text-white mb-6">Contact Information</h3>
              <div className="space-y-4">
                <a 
                  href="mailto:info@arkglobalsolutions.co.uk"
                  className="flex items-center gap-4 text-gray-200 hover:text-white transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Mail size={20} className="text-white" />
                  </div>
                  <span>info@arkglobalsolutions.co.uk</span>
                </a>
                <a 
                  href="https://www.arkglobalsolutions.co.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 text-gray-200 hover:text-white transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Globe size={20} className="text-white" />
                  </div>
                  <span>arkglobalsolutions.co.uk</span>
                </a>
                <div className="flex items-center gap-4 text-gray-200">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <MapPin size={20} className="text-white" />
                  </div>
                  <span>124 City Road, London, EC1V 2NX</span>
                </div>
                <div className="flex items-center gap-4 text-gray-200">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Building2 size={20} className="text-white" />
                  </div>
                  <span>Registered in England & Wales</span>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className="text-gray-400 mb-4">Prefer email directly?</p>
              <a
                href="mailto:info@arkglobalsolutions.co.uk"
                className="inline-flex items-center gap-2 text-[#60a5fa] hover:underline"
              >
                <Mail size={18} /> info@arkglobalsolutions.co.uk
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
    <Footer />
  </div>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-8 border-t border-gray-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="ARK Global Solutions" className="h-8 w-auto" />
          </div>
          <div className="flex items-center gap-6">
            <Link to="/blog" className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors">Blog</Link>
            <Link to="/careers" className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors">Careers</Link>
            <Link to="/contact" className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors">Contact</Link>
          </div>
          <div className="flex items-center gap-4">
            <a href="https://ico.org.uk/" target="_blank" rel="noopener noreferrer" title="Information Commissioner's Office" className="bg-black rounded p-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_d7937107-a351-4fc0-829f-c77a54cc199e/artifacts/94prgz1m_Information_Commissioners_Office_logo.388db5f2.webp" 
                alt="ICO - Information Commissioner's Office" 
                className="h-8 w-auto"
              />
            </a>
            <div className="bg-black rounded p-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_d7937107-a351-4fc0-829f-c77a54cc199e/artifacts/csopunxq_made-in-britain-logo-white.519a2cb0.webp" 
                alt="Made in Britain" 
                className="h-8 w-auto"
              />
            </div>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-gray-100 text-center">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} ARK Global Solutions Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Job Listings Data
const jobListings = [
  {
    id: "senior-fullstack-developer",
    title: "Senior Full-Stack Developer",
    department: "Software Development",
    location: "Remote / UK",
    type: "Full-time",
    salary: "£60,000 - £80,000",
    description: "We're looking for an experienced Full-Stack Developer to join our growing software team. You'll work on exciting projects including our flagship STAYRYTE platform and client applications.",
    requirements: [
      "5+ years of experience in full-stack development",
      "Strong proficiency in React.js, Next.js, and Node.js",
      "Experience with PostgreSQL, MongoDB, or similar databases",
      "Familiarity with cloud services (AWS, Supabase)",
      "Excellent problem-solving and communication skills",
      "UK work authorisation required"
    ],
    benefits: [
      "Competitive salary with annual reviews",
      "Remote-first working environment",
      "25 days annual leave + bank holidays",
      "Professional development budget",
      "Pension contributions",
      "Flexible working hours"
    ]
  },
  {
    id: "hr-compliance-specialist",
    title: "HR & Compliance Specialist",
    department: "HR Management",
    location: "London / Hybrid",
    type: "Full-time",
    salary: "£40,000 - £55,000",
    description: "Join our HR team to support clients with sponsor licence management, Right to Work compliance, and general HR administration. You'll work directly with businesses to ensure they meet UKVI requirements.",
    requirements: [
      "3+ years experience in HR or compliance roles",
      "Strong knowledge of UK employment law",
      "Experience with UKVI sponsor licence management",
      "Understanding of Right to Work verification processes",
      "Excellent attention to detail",
      "Strong organisational and communication skills"
    ],
    benefits: [
      "Hybrid working (3 days office, 2 days remote)",
      "25 days annual leave + bank holidays",
      "Professional qualification support",
      "Pension contributions",
      "Health and wellness benefits",
      "Career progression opportunities"
    ]
  },
  {
    id: "recruitment-consultant",
    title: "Recruitment Consultant",
    department: "Staffing Solutions",
    location: "London / Hybrid",
    type: "Full-time",
    salary: "£30,000 - £45,000 + Commission",
    description: "We're expanding our staffing team and looking for driven Recruitment Consultants to source and place candidates across various sectors. This role offers excellent earning potential with uncapped commission.",
    requirements: [
      "1-3 years recruitment experience (agency or in-house)",
      "Strong communication and negotiation skills",
      "Ability to build and maintain client relationships",
      "Target-driven mindset",
      "Knowledge of UK employment regulations",
      "Experience with ATS systems preferred"
    ],
    benefits: [
      "Base salary + uncapped commission",
      "Hybrid working arrangement",
      "Clear career progression path",
      "Team incentives and bonuses",
      "Training and development programmes",
      "Modern office in central London"
    ]
  },
  {
    id: "business-development-manager",
    title: "Business Development Manager",
    department: "Business Support",
    location: "Remote / UK",
    type: "Full-time",
    salary: "£50,000 - £70,000 + Bonus",
    description: "Lead our business development efforts to grow ARK Global's client base across all service lines. You'll identify opportunities, nurture relationships, and help shape our go-to-market strategy.",
    requirements: [
      "5+ years in B2B sales or business development",
      "Experience in software, HR, or professional services sectors",
      "Proven track record of meeting and exceeding targets",
      "Strong presentation and proposal writing skills",
      "Ability to understand and articulate technical solutions",
      "Existing network in UK business community preferred"
    ],
    benefits: [
      "Competitive base + performance bonus",
      "Remote working with travel to client meetings",
      "Company car or car allowance",
      "25 days annual leave + bank holidays",
      "Pension contributions",
      "Laptop and home office setup provided"
    ]
  }
];

// Careers Page
const CareersPage = () => {
  const [selectedJob, setSelectedJob] = useState(null);

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <SEO 
        title="Careers at ARK Global Solutions | Job Opportunities in Software, HR & Staffing UK"
        description="Join ARK Global Solutions Ltd. Explore career opportunities in software development, HR management, staffing, and business support. Remote and London-based positions available."
        keywords="ARK Global careers, software developer jobs UK, HR jobs London, staffing recruitment jobs, tech careers UK, remote developer jobs"
      />
      <Navigation isHome={false} />
      
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2563eb]/20 via-transparent to-[#60a5fa]/10" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <p className="text-[#60a5fa] text-sm font-medium tracking-wider uppercase mb-4">Join Our Team</p>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            <span className="text-[#60a5fa]">Careers</span> at ARK Global
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl">
            Build your career with a dynamic, growing company. We're always looking for talented individuals who share our passion for delivering exceptional solutions.
          </p>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl font-semibold text-white mb-8">Why Join ARK Global?</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: "Innovation", desc: "Work on cutting-edge projects using the latest technologies" },
              { icon: Users, title: "Great Team", desc: "Collaborate with talented professionals across disciplines" },
              { icon: Trophy, title: "Growth", desc: "Clear career progression and development opportunities" },
              { icon: Handshake, title: "Flexibility", desc: "Remote and hybrid options to suit your lifestyle" }
            ].map((item, idx) => (
              <div key={idx} className="p-6 rounded-xl bg-black/40 border border-white/5">
                <item.icon className="text-[#60a5fa] mb-4" size={28} strokeWidth={1.5} />
                <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section data-testid="job-listings" className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl font-semibold text-white mb-8">Open Positions</h2>
          <div className="space-y-4">
            {jobListings.map((job) => (
              <div
                key={job.id}
                data-testid={`job-card-${job.id}`}
                className="rounded-xl bg-white/5 border border-white/10 hover:border-[#60a5fa]/30 transition-all overflow-hidden"
              >
                <button
                  onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
                  className="w-full p-6 text-left"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">{job.title}</h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Building2 size={14} /> {job.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin size={14} /> {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} /> {job.type}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-[#60a5fa] font-medium">{job.salary}</span>
                      <ChevronDown 
                        className={`text-gray-400 transition-transform ${selectedJob === job.id ? 'rotate-180' : ''}`} 
                        size={20} 
                      />
                    </div>
                  </div>
                </button>

                {selectedJob === job.id && (
                  <div className="px-6 pb-6 border-t border-white/10 pt-6">
                    <p className="text-gray-300 mb-6">{job.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-semibold text-white mb-3">Requirements</h4>
                        <ul className="space-y-2">
                          {job.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-400">
                              <span className="text-[#60a5fa] mt-1">•</span>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-3">Benefits</h4>
                        <ul className="space-y-2">
                          {job.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-400">
                              <span className="text-[#60a5fa] mt-1">•</span>
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-white/10">
                      <a
                        href={`mailto:hr@arkglobalsolutions.co.uk?subject=Application: ${job.title}`}
                        data-testid={`apply-${job.id}`}
                        className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#3b82f6] text-white px-6 py-3 rounded-full font-bold transition-all"
                      >
                        <Mail size={18} /> Apply Now
                      </a>
                      <p className="text-sm text-gray-500 mt-3">
                        Send your CV and cover letter to hr@arkglobalsolutions.co.uk
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-2xl font-semibold text-white mb-4">Don't see the right role?</h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            We're always interested in hearing from talented individuals. Send us your CV and we'll keep you in mind for future opportunities.
          </p>
          <a
            href="mailto:hr@arkglobalsolutions.co.uk?subject=General Application"
            className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#3b82f6] text-white px-8 py-4 rounded-full font-medium transition-all"
          >
            <Mail size={18} /> Send Your CV
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Blog List Page
const BlogListPage = () => {
  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <SEO 
        title="Blog | AI, HR Compliance & Business Insights | ARK Global Solutions"
        description="Expert insights on AI in HR, UKVI compliance, sponsor licence management, Right to Work verification, and business technology. Resources for UK employers from ARK Global Solutions."
        keywords="HR compliance blog, AI HR technology, UKVI sponsor licence guide, Right to Work UK, immigration compliance tips, HR automation articles, business technology insights"
      />
      <Navigation isHome={false} />
      
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2563eb]/20 via-transparent to-[#60a5fa]/10" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <p className="text-[#60a5fa] text-sm font-medium tracking-wider uppercase mb-4">Insights & Resources</p>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            Our <span className="text-[#60a5fa]">Blog</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl">
            Expert insights on AI, HR management, immigration compliance, and business technology from the ARK Global Solutions team.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section data-testid="blog-list" className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                data-testid={`blog-card-${post.id}`}
                className="group rounded-2xl bg-white/5 border border-white/10 hover:border-[#60a5fa]/50 overflow-hidden transition-all duration-300"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 rounded-full bg-[#2563eb]/30 text-[#60a5fa] text-xs font-medium">
                      {post.category}
                    </span>
                  </div>
                  <h2 className="text-xl font-semibold text-white mb-3 group-hover:text-[#60a5fa] transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} /> {post.readTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Blog Post Page
const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.id === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="bg-[#0A0A0A] min-h-screen flex items-center justify-center">
        <SEO 
          title="Post Not Found | ARK Global Solutions Blog"
          description="The requested blog post could not be found."
          keywords="ARK Global Solutions blog"
        />
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[#60a5fa] hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <SEO 
        title={`${post.title} | ARK Global Solutions Blog`}
        description={post.metaDescription || post.excerpt}
        keywords={post.metaKeywords || post.tags.join(', ')}
      />
      <Navigation isHome={false} />
      
      {/* Article Header */}
      <article data-testid="blog-post" className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Back Button */}
          <button
            onClick={() => navigate('/blog')}
            data-testid="back-to-blog"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={18} /> Back to Blog
          </button>

          {/* Category & Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-4 py-1.5 rounded-full bg-[#2563eb]/30 text-[#60a5fa] text-sm font-medium">
              {post.category}
            </span>
            <span className="flex items-center gap-1 text-gray-400 text-sm">
              <Calendar size={16} /> {post.date}
            </span>
            <span className="flex items-center gap-1 text-gray-400 text-sm">
              <Clock size={16} /> {post.readTime}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            {post.title}
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-gray-300 leading-relaxed mb-8">
            {post.excerpt}
          </p>

          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden mb-12">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-auto"
            />
          </div>

          {/* Content */}
          <div 
            className="prose prose-invert prose-lg max-w-none
              prose-headings:font-semibold prose-headings:text-white
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:my-4 prose-li:text-gray-300 prose-li:mb-2
              prose-strong:text-white prose-strong:font-semibold"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={18} className="text-gray-500" />
              {post.tags.map((tag, idx) => (
                <span 
                  key={idx}
                  className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm text-gray-400"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-[#2563eb] to-[#1e40af] border border-white/10">
            <h3 className="text-2xl font-semibold text-white mb-4">
              Need Help with HR Compliance?
            </h3>
            <p className="text-gray-200 mb-6">
              ARK Global Solutions can help you navigate the complexities of UK HR compliance, from sponsor licence management to AI-powered verification systems.
            </p>
            <a
              href="mailto:info@arkglobalsolutions.co.uk"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-[#2563eb] px-6 py-3 rounded-full font-bold transition-all"
            >
              <Mail size={18} /> Contact Us Today
            </a>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl font-semibold text-white mb-8">Related Articles</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {blogPosts.filter(p => p.id !== post.id).slice(0, 3).map((relatedPost) => (
              <Link
                key={relatedPost.id}
                to={`/blog/${relatedPost.id}`}
                className="group rounded-xl bg-black/40 border border-white/5 hover:border-[#60a5fa]/30 overflow-hidden transition-all"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={relatedPost.image} 
                    alt={relatedPost.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-white group-hover:text-[#60a5fa] transition-colors line-clamp-2">
                    {relatedPost.title}
                  </h3>
                  <p className="text-sm text-gray-500 mt-2">{relatedPost.readTime}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Home Page
const HomePage = () => {
  return (
    <>
      <SEO 
        title="ARK Global Solutions | Software Development, Staffing & HR Compliance UK"
        description="ARK Global Solutions Ltd delivers bespoke software development, staffing solutions, HR management, and UKVI compliance services to businesses across the UK. Based in London."
        keywords="software development UK, staffing solutions London, HR management, UKVI compliance, sponsor licence management, Right to Work checks, React development, mobile app development"
      />
      <Navigation isHome={true} />
      <Hero />
      <About />
      <Services />
      <Projects />
      <WhyUs />
      <Footer />
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="App bg-[#0A0A0A] min-h-screen">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
